import { articleMetaBySlug } from "./meta";

export const meta = articleMetaBySlug["why-running-injuries-happen"]!;

export function Body() {
  return (
    <>
      <p>
        Many runners get injured at some point, and the advice is often a simple rule about how
        much you can add each week. Research on running injuries points to a more useful way of
        thinking about it: load against capacity.
      </p>

      <h2>A framework from Danish research</h2>
      <p>
        Researchers led from Aarhus University described in 2017 how running injuries develop.
        Every structure in the body, such as a tendon, a bone or a muscle, has a certain capacity
        when you start a run. Each run puts load on it. During the run the capacity can drop, for
        example with fatigue. An injury develops when the load on a structure exceeds what it
        can handle.
      </p>
      <p>
        The framework explains why the same run can be fine one day and too much another, and
        why the same weekly mileage can suit one runner and not the next.
      </p>

      <h2>Why simple ratios fall short</h2>
      <p>
        A popular way to manage load compares the last week with the last four weeks. A review
        from 2020 concluded that there is no evidence supporting this ratio for reducing injury
        risk. Its causal relationship with injury has not been established, and the calculation
        itself has statistical problems.
      </p>
      <p>
        A large study of 5,205 runners from 2025 pointed in the same direction. A single run that
        was much longer than the longest run of the previous month was linked to more overuse
        injuries, while the weekly ratio was not.
      </p>

      <h2>What it means for you</h2>
      <p>
        Think about each run, not just the weekly total. Increase your long run in small steps.
        Be careful after a break, when your capacity is lower than before. And take tiredness,
        poor sleep and new pain seriously, because they lower your capacity on the day.
      </p>
      <p>
        If something hurts in a way that changes how you run, stop and get it checked.
      </p>

      <h2>What the research can't tell you</h2>
      <p>
        The framework explains how injuries arise, but it doesn't give a number for how much
        load you can tolerate. No study can predict your individual risk.
      </p>

      <h2>How we use it</h2>
      <p>
        PX7 Training compares a planned run with your longest run in the past 30 days and flags
        big jumps. It asks how you feel before a session, and pain or illness pauses the normal
        plan.
      </p>

      <h2>Sources</h2>
      <ul>
        <li>
          Bertelsen et al. (2017), A framework for the etiology of running-related injuries.{" "}
          <a href="https://doi.org/10.1111/sms.12883">Scandinavian Journal of Medicine & Science in Sports</a>.
        </li>
        <li>
          Impellizzeri et al. (2020), Acute:chronic workload ratio: conceptual issues and
          fundamental pitfalls.{" "}
          <a href="https://doi.org/10.1123/ijspp.2019-0864">International Journal of Sports Physiology and Performance</a>.
        </li>
        <li>
          Frandsen et al. (2025), How much running is too much? Identifying high-risk running
          sessions in a 5200-person cohort study.{" "}
          <a href="https://doi.org/10.1136/bjsports-2024-109380">British Journal of Sports Medicine</a>.
        </li>
      </ul>
    </>
  );
}
